<template>
	<div class="bottom-other-payment">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)">
			<div class="bottom-other-content" v-if="active">
				<ModalHeader :title="getString('other_payments')" />
				<div class="pm-container">
					<div v-for="paymentMethod in paymentMethods" :key="paymentMethod.value" class="pm" :class="{fullWidth: paymentMethods.length <= 1, 'has-shortcut': paymentMethod.value === 'manual' && cashmaticLinked}">
						<AppButton :text="paymentMethod.name" :outline="!paymentMethod.primary" @click="confirmPayment(paymentMethod.value)" />
						<div class="payment-shortcut" v-if="paymentMethod.value === 'manual' && cashmaticLinked" v-html="svgStore.get('iconCashmatic')" @click.stop="startManualCashmatic"></div>
					</div>
				</div>
			</div>
		</AppBottomSheet>
		<CustomersPicker :active="customerPicker" @toggling-popup="togglingCustomersPicker" @picked-customer="pickedCustomer" />
		<AppPopup
			:title="getString('invoice_paid_popup_title')"
			ref="already-paid-invoice-popup"
			:buttons="popupButtons" >
			<div>
				<p>{{getString('invoice_paid_popup_text')}}</p>
				<div class="col-12 centered p-20">
					<AppSwitch :label="getString('paid')" :checked="counterStore.setInvoiceAsPaid" @switched="switchInvoicePaid" />
					<AppDropdown :collapsed="counterStore.setInvoiceAsPaid">
						<div class="row centered">
							<div>
								<p class="mb-3"><small>{{ getString('payment_due_date') }}</small></p>
								<DatePicker ref="date-picker" mode="date" color="teal" v-model="paymentDueDate" @update:modelValue="updatedDueDate" :min-date='new Date()' :max-date='nextSixMonths' :is-dark="userStore.darkMode">
									<template #default="{ togglePopover }">
										<AppTextInput class="ml-auto text-input" ref="payment-due-date" @click="togglePopover()" readonly :placeholder="getString('payment_due_date')" :value="paymentDueDate ? formatDateYYMMDD(paymentDueDate) : null" />
									</template>
								</DatePicker>
							</div>
						</div>
					</AppDropdown>
				</div>
			</div>
		</AppPopup>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import {useCounterStore} from "@/console-new/stores/counter.store";
import AppButton from "@/shared/components/AppButton.vue";
import CustomersPicker from "@/console-new/components/ops/CustomersPicker.vue";
import {useUserStore} from "@/console-new/stores";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {formatDateYYMMDD} from "@/shared/helpers/dates";
import {isAndroid} from "@/shared/helpers/webapp";

export default {
	setup() {
		let languageStore = useLanguageStore();
		const counterStore = useCounterStore();
		const userStore = useUserStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, languageStore, counterStore, formatDateYYMMDD, userStore, svgStore};
	},
	name: "BottomOtherPayment",
	components: {AppDropdown, AppPopup, AppSwitch, AppTextInput, CustomersPicker, AppButton, AppBottomSheet, ModalHeader},
	emits: ["toggling-popup", "payment-done", "manual-cashmatic"],
	mixins: [WindowWidth],
	props: {
		active: {
			type: Boolean,
			required: true,
		},
		noPayLater: {
			type: Boolean,
			default: false,
		},
		isRegistry: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			nextSixMonths: new Date(new Date().setDate(new Date().getDate() + 180)),
			nextMonth: new Date(new Date().setDate(new Date().getDate() + 30)),
			customerPicker: false,
			currentPm: null,
			paymentDueDate: null,
		}
	},
	computed: {
		popupButtons() {
			return [
				{text: this.getString('cancel'), negative: true},
				{text: this.getString('continue'), callback: this.confirmAlreadyPaidInvoice}
			]
		},
		bottomSheetWidth() {
			if (this.windowWidth < 800) {
				return "100%";
			} else {
				return "550px";
			}
		},
		cashmaticLinked() {
			return isAndroid() && localStorage.getItem("cashmaticActive") === "1";
		},
		paymentMethods() {
			let pms = [];
			if(!this.noPayLater && this.userStore.currBusiness.counter_pay_later) {
				pms.push({
					name: this.getString("paylater"),
					value: "paylater",
					primary: true,
				})
			}
			if(this.userStore.currBusiness.counter_ticket){
				pms.push({
					name: this.getString("ticket"),
					value: "ticket",
					primary: false,
				});
			}
			if(this.userStore.currBusiness.counter_satispay && this.userStore.currBusiness.country === 'IT'){
				pms.push({
					name: this.getString("satispay"),
					value: "satispay",
					primary: false,
				});
			}
			if(this.userStore.currBusiness.counter_wire_transfer){
				pms.push({
					name: this.getString("wire_transfer"),
					value: "wire",
					primary: false,
				});
			}
			if(this.userStore.currBusiness.counter_check){
				pms.push({
					name: this.getString("pm_check"),
					value: "check",
					primary: false,
				});
			}
			if(this.userStore.currBusiness.counter_manual && !this.counterStore.creditTopUpMode) {
				pms.push({
					name: this.getString("manual"),
					value: "manual",
					primary: false,
				});
			}
			return pms;
		}
	},
	methods: {
		switchInvoicePaid(checked) {
			this.counterStore.setInvoiceAsPaid = !!checked;
			if(this.counterStore?.invoiceAdditionalData) {
				if(!!checked && this.paymentDueDate) {
					this.paymentDueDate = null;
				} else if(!checked && !this.paymentDueDate) {
					this.paymentDueDate = this.nextMonth;
				}
				this.counterStore.invoiceAdditionalData.payment_due_date = this.paymentDueDate;
			}
		},
		confirmAlreadyPaidInvoice() {
			this.$refs['already-paid-invoice-popup'].toggleStatus(false);
			this.$emit('toggling-popup', false);
			this.$emit('payment-done', this.currentPm);
		},
		togglingCustomersPicker(v) {
			if(!v) {
				this.customerPicker = false;
			}
		},
		pickedCustomer(customer) {
			this.customerPicker = false;
			this.counterStore.selectedCustomer = customer;
			this.$emit('toggling-popup', false);
			this.$emit('payment-done', "paylater");
		},
		updatedDueDate() {
			if(this.counterStore?.invoiceAdditionalData) {
				this.counterStore.invoiceAdditionalData.payment_due_date = this.paymentDueDate;
			}
		},
		startManualCashmatic() {
			this.$emit('toggling-popup', false);
			this.$emit('manual-cashmatic');
		},
		confirmPayment(pm) {
			this.currentPm = null;
			if(pm === "paylater") {
				if(this.counterStore.selectedCustomer) {
					this.$emit('toggling-popup', false);
					this.$emit('payment-done', pm);
				} else {
					this.customerPicker = true;
				}
			} else if((pm === 'wire' || pm === 'check') && ((this.counterStore?.selectedCustomer?.type === 'company' && !this.isRegistry) || (this.counterStore?.registryEditingCustomer?.type === 'company' && this.isRegistry))) {

				this.counterStore.setInvoiceAsPaid = true;
				if(this.counterStore?.invoiceAdditionalData) {
					this.paymentDueDate = null;
					this.counterStore.invoiceAdditionalData.payment_due_date = null;
				}

				this.currentPm = pm;
				this.$refs['already-paid-invoice-popup'].toggleStatus(true);

			} else {
				this.$emit('toggling-popup', false);
				this.$emit('payment-done', pm);
			}
		}
	}
}
</script>

<style scoped lang="scss">
.pm-container {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px;
	margin-top: 20px;

	.pm {
		width: calc(50% - 5px);
		position: relative;
		.base-button {
			width: 100%;
			max-width: unset;
			height: 100px;
		}
		.fullWidth {
			width: 100%;
		}

		&.has-shortcut {
			.base-button {
				padding-right: 60px;
			}
		}

		.payment-shortcut {
			position: absolute;
			top: 5px;
			right: 5px;
			width: 50px;
			height: 90px;
			border-radius: 10px;
			display: flex;
			justify-content: center;
			align-items: center;
			box-shadow: 0 2px 3px rgba(7, 82, 163, 0.15);
			border: 1px solid $primary;
			cursor: pointer;

			:deep(svg) {
				width: 18px;

				.fill {
					fill: var(--color-svg-primary);
				}

				.stroke {
					stroke: var(--color-svg-primary);
				}
			}
		}
	}
}
</style>